import { useState, useEffect, useRef, useCallback } from "react";
import { DateTime } from "luxon";
import i18n from "../../utils/i18n";
import Meals from "./Meals";
import DateAndTimeInput from "./DateAndTimeInput";
import { calculateMeals } from "../../utils/calculateMeals";
import { getDateTime } from "../../utils/getDateTime";
import {
  getDateTimeFromPicker,
  getTimeFromInput,
} from "../../utils/dateTimeConverters";

const CopyShiftForm = ({ shift, event, onSubmitHandler, hideModal }) => {
  const formRef = useRef(null);
  const startTimeInputRef = useRef(null);
  const endTimeInputRef = useRef(null);

  const [startDate, setStartDate] = useState(shift.start_time);
  const [startTime, setStartTime] = useState(shift.start_time);
  const [endDate, setEndDate] = useState(shift.end_time);
  const [endTime, setEndTime] = useState(shift.end_time);
  const [lunch, setLunch] = useState(shift.lunch);
  const [dinner, setDinner] = useState(shift.dinner);
  const [validationError, setValidationError] = useState(null);

  // Initialize inputmask on mount. Inputmask's own API rather than its jQuery plugin, so this
  // component does not need jQuery from the Sprockets bundle to mask its time fields.
  useEffect(() => {
    if (!formRef.current) return;

    // Inputmask's own API rather than its jQuery plugin, and one instance per field (an instance
    // holds per-element state). `window.Inputmask` is the copy the Sprockets bundle already loads:
    // importing the npm package here instead puts a second copy on the page, and the two of them
    // leave the field masked but inert.
    formRef.current.querySelectorAll(".time_input").forEach((input) => {
      window.Inputmask({
        alias: "datetime",
        inputFormat: "HH:mm",
        placeholder: i18n.t("schedule.shifts.form.inputmask_placeholder"),
        showMaskOnHover: false,
      }).mask(input);
    });
  }, []);

  // Calculate meals when dates/times change
  useEffect(() => {
    const state = { startDate, startTime, endDate, endTime };
    const startDateTime = getDateTime("start", state);
    const endDateTime = getDateTime("end", state);

    if (startDateTime && endDateTime) {
      const meals = calculateMeals(startDateTime, endDateTime);
      setLunch(meals.lunch);
      setDinner(meals.dinner);
    }
  }, [startDate, startTime, endDate, endTime]);

  const validate = () => {
    setValidationError(null);

    const state = { startDate, startTime, endDate, endTime };
    const startDateTime = getDateTime("start", state);
    const endDateTime = getDateTime("end", state);

    if (
      !startDateTime ||
      !startDateTime.isValid ||
      !endDateTime ||
      !endDateTime.isValid
    ) {
      setValidationError(
        i18n.t("activemodel.errors.models.shift.invalid_date_and_time")
      );
      return false;
    }

    if (endDateTime < startDateTime) {
      setValidationError(
        i18n.t("activemodel.errors.models.shift.end_time_before_start_time")
      );
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      const state = { startDate, startTime, endDate, endTime };
      onSubmitHandler({
        start_time: getDateTime("start", state),
        end_time: getDateTime("end", state),
        lunch: lunch,
        dinner: dinner,
      });
    }
  };

  // Check if the shift extends past midnight based on state values
  const shiftExtendsPastMidnight = useCallback((start, end) => {
    const toDateTime = (value) => value instanceof DateTime ? value : DateTime.fromISO(value);
    const startDt = toDateTime(start);
    const endDt = toDateTime(end);
    return startDt?.isValid && endDt?.isValid && !startDt.hasSame(endDt, 'day');
  }, []);

  const startDateHandleChange = (e) => {
    const dateTime = getDateTimeFromPicker(e.date);
    if (!dateTime) return;

    setStartDate(dateTime);

    // Check if the shift extends past midnight with the new start date and current end date
    // If it does, set end date to the next day; otherwise, set to the same day
    if (shiftExtendsPastMidnight(dateTime, endDate)) {
      setEndDate(dateTime.plus({ days: 1 }));
    } else {
      setEndDate(dateTime);
    }

    startTimeInputRef.current?.focus();
  };

  const startTimeHandleChange = (e) => {
    const parsedTime = getTimeFromInput(e.target.value);
    setStartTime(parsedTime);
  };

  const endDateHandleChange = (e) => {
    const dateTime = getDateTimeFromPicker(e.date);
    if (!dateTime) return;

    setEndDate(dateTime);
    endTimeInputRef.current?.focus();
  };

  const endTimeHandleChange = (e) => {
    const parsedTime = getTimeFromInput(e.target.value);
    setEndTime(parsedTime);
  };

  return (
    <form ref={formRef} className="form copy-form" onSubmit={handleSubmit}>
      <div className="modal-body">
        {validationError && (
          <div className="alert alert-danger">{validationError}</div>
        )}
        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.from")}</label>
          <DateAndTimeInput
            type="start"
            selectedDate={startDate}
            selectedTime={startTime}
            event={event}
            onDateChange={startDateHandleChange}
            onTimeBlur={startTimeHandleChange}
            timeInputRef={startTimeInputRef}
          />
        </div>
        <div className="form-group">
          <label>{i18n.t("schedule.shifts.form.to")}</label>
          <DateAndTimeInput
            type="end"
            selectedDate={endDate}
            selectedTime={endTime}
            event={event}
            onDateChange={endDateHandleChange}
            onTimeBlur={endTimeHandleChange}
            timeInputRef={endTimeInputRef}
          />
        </div>
      </div>

      <Meals lunch={lunch} dinner={dinner} />

      <div className="modal-footer">
        <input
          type="submit"
          className="btn btn-primary"
          value={i18n.t("schedule.shifts.form.copy")}
        />
        <button type="button" className="btn btn-secondary" onClick={hideModal}>
          Close
        </button>
      </div>
    </form>
  );
};

export default CopyShiftForm;
